import { useEffect, useRef, useState } from 'react';

// One-shot IntersectionObserver hook. `inView` flips to true the first
// time the element crosses the viewport threshold; after that the
// observer disconnects and inView stays true. Used by Reveal + CountUp
// to fire entrance animations exactly once.
export function useInView<T extends Element = HTMLDivElement>(opts: {
  rootMargin?: string;
  threshold?: number;
} = {}): { ref: React.RefObject<T>; inView: boolean } {
  const { rootMargin = '0px 0px -10% 0px', threshold = 0.1 } = opts;
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin, threshold },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin, threshold]);

  // @ts-ignore
  return { ref, inView };
}
